import React, { useState, useEffect, useRef } from "react";
import { Timeline } from "@material-tailwind/react";
import { AiOutlineClose } from "react-icons/ai";
import { ArrowRight, Download, Plus } from "lucide-react";
import Papa from "papaparse";
import ManualUpload from "./ManualUpload";
import {
  fetchPropertyConfigurations,
  fetchPropertyData,
} from "../services/projects";
import { toast } from "react-toastify";
import axiosInstance from "../services/api/axios";
import { useUser } from "../context/UserContext";
import { PrimaryButton, SecondaryButton, IconButton } from "./Buttons";
import CustomSelect from "./CustomSelect";
import { FormModal } from "./form";

const BulkUploadProperties = ({
  onFileUpload,
  projectId,
  project,
  onUploadSuccess,
  setProperties,
  phaseId
}) => {
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState(0);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [errors, setErrors] = useState([]);
  const [errorType, setErrorType] = useState("");
  const [uploadMethod, setUploadMethod] = useState("csv");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [configurations, setConfigurations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [backendUploadMessage, setBackendUploadMessage] = useState("");
  const { userInfo } = useUser();
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [isManualUploadOpen, setIsManualUploadOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [buildings, setBuildings] = useState([]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const toggleManualUpload = () => {
    setIsManualUploadOpen(!isManualUploadOpen);
  };
  const resetState = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setStep(0);
    setIsUploadSuccess(false);
    setErrors([]);
    setErrorType("");
    setBackendUploadMessage("");
    setIsFileUploaded(false);
  };

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await axiosInstance.get("/projects/buildings", {
          params: {
            projectId: projectId,
            projectPhaseId: phaseId,
            customerId: userInfo.loginIdentifierDetails.customer.customerId ||userInfo.customerId,
            //customerId: userInfo.loginIdentifierDetails.customer.customerId,
          },
        });
        
        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        let buildingsArray = [];
        if (response?.items && Array.isArray(response.items)) {
          buildingsArray = response.items;
        } else if (Array.isArray(response)) {
          buildingsArray = response;
        }
        
        setBuildings(buildingsArray);
      } catch (error) {
        console.error("Error fetching buildings:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBuildings();
  }, [projectId, userInfo.loginIdentifierDetails.customer.customerId, phaseId]);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const toggleBulkUploadModal = () => {
    setIsBulkUploadModalOpen(!isBulkUploadModalOpen);
    if (isBulkUploadModalOpen) {
      resetState();
    }
  };

  const handleConfigChange = (event) => {
    event.stopPropagation();
    setSelectedConfigId(event.target.value);
  };
  const handleBuildingChange = async (event) => {
    event.stopPropagation();
    const buildingId = event.target.value;
    setSelectedBuildingId(buildingId);
    setSelectedConfigId("");

    try {
      setIsLoading(true);
      const response = await fetchPropertyConfigurations({
        //projectId: projectId,
        //customerId: userInfo.loginIdentifierDetails.customer.customerId,
        buildingId: buildingId,
        projectPhaseId: phaseId,
        customerId: userInfo.loginIdentifierDetails.customer.customerId ||userInfo.customerId,
      });
      
      // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
      let configData = [];
      if (response?.items && Array.isArray(response.items)) {
        configData = response.items;
      } else if (Array.isArray(response)) {
        configData = response;
      }
      
      setConfigurations(configData);
    } catch (error) {
      console.error("Error fetching configurations:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSelectMethod = (method) => {
    setUploadMethod(method);
    setDropdownOpen(false);
    if (method === "csv") {
      toggleBulkUploadModal();
    } else if (method === "manual") {
      toggleManualUpload();
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);


  const handleFileChange = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file =
      e.target.files?.[0] || (e.dataTransfer ? e.dataTransfer.files[0] : null);

    if (file) {
      setSelectedFile(file);
      setUploadProgress(0);
      setIsUploading(true);
      setIsFileUploaded(true);
    }
  };

  const uploadCSV = async () => {
    if (!selectedFile || !selectedConfigId || !selectedBuildingId) {
      toast.error("Please select a file, configuration, and building.");
      return;
    }

    setIsSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append("csv_file", selectedFile);

      const response = await axiosInstance.post(
        `/property/developer/bulk-insert?propertyConfigId=${selectedConfigId}&buildingId=${selectedBuildingId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.success) {
        setIsUploadSuccess(true);
        setStep(3);
        setBackendUploadMessage(response.message);

        // Show toast message after successful upload
        toast.success( "CSV uploaded successfully!");

        // Refetch properties and update state.
        const updatedProperties = await fetchPropertyData(
          projectId,
          project?.customerId,
          phaseId
        );

        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        let propertiesArray = [];
        if (updatedProperties?.items && Array.isArray(updatedProperties.items)) {
          propertiesArray = updatedProperties.items;
        } else if (Array.isArray(updatedProperties)) {
          propertiesArray = updatedProperties;
        }

        setProperties(propertiesArray); // Call the setProperties function passed as a prop

        // Call the onUploadSuccess callback to trigger immediate refresh
        if (onUploadSuccess) {
          onUploadSuccess();
        }

        // Close the modal after successful upload
        toggleBulkUploadModal();

      } else {
        setIsUploadSuccess(false);
        setBackendUploadMessage(response.message);
      }
    } catch (error) {
      let errorMessage = "";
      if (error.response && error.response.status === 404) {
       
        errorMessage =
          error.response?.data?.detail ||
          "The specified resource could not be found. Please check the URL or configurations.";
        toast.error(errorMessage);
      } else if (error.response && error.response.status === 409) {
      
        errorMessage =
          error.response?.data?.detail ||
          "The specified resource could not be found. Please check the URL or configurations.";
        toast.error(errorMessage);
      } else if (error.response && error.response.status === 422) {
        errorMessage =
          "Validation Error: " +
          (error.response.data?.detail || "Please check your CSV data.");
        toast.error(errorMessage);
        setIsUploadSuccess(false);
      } else {
        errorMessage =
          error.response?.data?.detail ||
          "An error occurred during the upload. Please try again.";
        toast.error(errorMessage);
      }
      setBackendUploadMessage(errorMessage);

      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    let interval;
    if (isUploading) {
      interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsUploading(false);
            return 100;
          }
          return prev + 20;
        });
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isUploading]);
  
  const handleCancelUpload = () => {
    setSelectedFile(null);
    setIsUploading(null);
    setUploadProgress(0);
    setBackendUploadMessage("");
    setIsFileUploaded(false);
  };

  const handleSubmit = () => {
    if (isFileUploaded) {
      uploadCSV();
    } else {
      toast.error("Please select a file to upload");
    }
  };
  return (
    // `relative` so the dropdown's absolute positioning anchors here
    // (against the button), not against the page; `z-30` keeps the
    // button container above the DataTable's refetch overlay (z-20).
    <div className="relative z-30">
      {/* Button to open the dropdown */}

      <IconButton
        onClick={toggleDropdown}
        icon={<Plus className="mr-1.5 size-3.5" />}
        left
        className="!w-auto !px-3 !py-1.5 text-sm"
      >
        Add
      </IconButton>

      {/* Dropdown menu — right-aligned to the button, z-50 so it sits above
          the DataTable below (which has an internal z-20 refetch overlay). */}
      {dropdownOpen && (
        <div
          className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50"
          ref={dropdownRef}
        >
          <button
            type="button"
            onClick={() => handleSelectMethod("manual")}
            className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-flamingo-50 hover:text-flamingo-700 transition-colors border-b border-slate-100"
          >
            Single
          </button>
          <button
            type="button"
            onClick={() => handleSelectMethod("csv")}
            className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-flamingo-50 hover:text-flamingo-700 transition-colors"
          >
            Bulk
          </button>
        </div>
      )}
      {uploadMethod === "csv" && (
        <FormModal
          open={isBulkUploadModalOpen}
          onClose={toggleBulkUploadModal}
          title="Bulk Upload Properties"
          size="xl"
          footer={
            step !== 3 ? (
              <div className="flex justify-between w-full gap-4">
                <SecondaryButton
                  className="w-32"
                  disabled={step === 0 || isSubmitLoading}
                  onClick={() => setStep(step - 1)}
                >
                  Previous
                </SecondaryButton>
                {step !== 2 ? (
                  <PrimaryButton
                    className="w-32 bg-flamingo-500 border-none"
                    icon={<ArrowRight className="ml-2 size-4" />}
                    disabled={isSubmitLoading}
                    onClick={() => setStep(step + 1)}
                  >
                    Next
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    className="w-32 bg-flamingo-500 border-none"
                    icon={isSubmitLoading ? null : <ArrowRight className="ml-2 size-4" />}
                    disabled={isSubmitLoading}
                    onClick={handleSubmit}
                  >
                    {isSubmitLoading ? "Loading..." : "Finish"}
                  </PrimaryButton>
                )}
              </div>
            ) : null
          }
        >
              <div className="w-full text-center">
                <Timeline
                  mode="stepper"
                  value={step}
                  onChange={(val) => setStep(Number(val))}
                  className="w-full p-8 px-28"
                >
                  <Timeline.Item
                    disabled={step < 0}
                    value={0}
                    className="w-full"
                  >
                    <Timeline.Header>
                      <Timeline.Separator />
                      <Timeline.Icon>1</Timeline.Icon>
                    </Timeline.Header>
                  </Timeline.Item>
                  <Timeline.Item
                    disabled={step < 1}
                    value={1}
                    className="w-full"
                  >
                    <Timeline.Header>
                      <Timeline.Separator />
                      <Timeline.Icon>2</Timeline.Icon>
                    </Timeline.Header>
                  </Timeline.Item>
                  <Timeline.Item disabled={step < 1} value={2}>
                    <Timeline.Header>
                      <Timeline.Separator />
                      <Timeline.Icon>3</Timeline.Icon>
                    </Timeline.Header>
                  </Timeline.Item>
                </Timeline>

                <div className="flex flex-col items-center ">
                  {step === 0 && (
                    <div className="mt-4 w-full p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
                        <CustomSelect
                          label="Select Building"
                          required
                          placeholder="Select Building"
                          options={buildings.map((b) => ({
                            value: b.buildingId,
                            label: b.buildingName,
                          }))}
                          value={
                            buildings
                              .map((b) => ({ value: b.buildingId, label: b.buildingName }))
                              .find((o) => o.value === selectedBuildingId) || null
                          }
                          onChange={(opt) =>
                            handleBuildingChange({
                              stopPropagation: () => {},
                              target: { value: opt?.value ?? "" },
                            })
                          }
                          styles={{}}
                          classNamePrefix="select"
                          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          size="form"
                        />
                        <CustomSelect
                          label="Select Configuration"
                          required
                          placeholder="Select Configuration"
                          options={configurations.map((c) => ({
                            value: c.propertyConfigId,
                            label: c.configName,
                          }))}
                          value={
                            configurations
                              .map((c) => ({ value: c.propertyConfigId, label: c.configName }))
                              .find((o) => String(o.value) === String(selectedConfigId)) || null
                          }
                          onChange={(opt) =>
                            handleConfigChange({
                              stopPropagation: () => {},
                              target: { value: opt?.value ?? "" },
                            })
                          }
                          isDisabled={!selectedBuildingId}
                          styles={{}}
                          classNamePrefix="select"
                          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          size="form"
                        />
                      </div>
                    </div>
                  )}
                  {step === 1 && (
                    <div className="">
                      <p className="font-medium text-xl pt-2">
                        Follow these steps to prepare your bulk upload:
                      </p>
                      <div className="w-full max-w-6xl bg-white shadow-md rounded-lg p-6 mt-2">
                        {/* Step 1 */}
                        <div className="pb-6">
                          <h3 className="font-semibold text-lg text-accent">
                            Step 1: Download the CSV Template
                          </h3>
                          <p className="text-gray-700 text-md mt-2 font-normal">
                            Download the CSV template provided below to start
                            your bulk upload process.
                          </p>

                          <a
                            href="/properties.csv"
                            download
                            className="bg-flamingo-500 items-center text-white py-2 px-4 rounded hover:bg-flamingo-600 transition mt-4 inline-block"
                          >
                            <Download className="w-6 h-6" />
                          </a>
                        </div>

                        {/* Step 2 */}
                        <div className="pb-6">
                          <h3 className="font-semibold text-lg text-accent">
                            Step 2: Add Your Property Data
                          </h3>
                          <p className="text-gray-700 text-md mt-2 font-normal">
                            Add your property data in the downloaded template
                            and save it as .csv .Make sure each field is
                            correctly filled.If you're using Excel, ensure that
                            you export or save the file as a .csv file format.
                          </p>
                        </div>

                        {/* Step 3 */}
                        <div className="pb-6">
                          <h3 className="font-semibold text-lg text-accent">
                            Step 3: Upload the CSV File
                          </h3>
                          <p className="text-gray-700 text-md mt-2 font-normal">
                            Once your template is filled and saved as a CSV,
                            upload the file using the form below.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {step === 2 && (
                  <div className="flex flex-col items-center mt-4">
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleFileChange}
                      onClick={(e) => {
                        if (e.target === e.currentTarget) {
                          document.getElementById("file-upload").click();
                        }
                      }}
                      className="w-full p-16 px-20 border-2 border-dashed border-gray-300 rounded-lg text-medium text-center hover:bg-blue-100 hover:border-accent hover:text-blue-800 transition duration-300 ease-in-out"
                    >
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        hidden
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer text-gray-500"
                      >
                        <span>Drag & Drop or Click to Upload CSV</span>
                      </label>
                    </div>

                    {selectedFile && (
                      <div className="mt-6 p-4 w-full bg-white border-2 border-dashed border-gray-300 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <img
                              src="/csv.png"
                              alt="CSV Icon"
                              className="w-8 h-8"
                            />
                            <span>{selectedFile.name}</span>
                          </div>
                          <div className="text-sm text-gray-600 ml-4">
                            {selectedFile.size && (
                              <span>
                                {Math.round(selectedFile.size / 1024)} KB
                              </span>
                            )}
                          </div>

                          <button
                            className="text-red-500 ml-4 hover:text-red-700 transition"
                            onClick={handleCancelUpload}
                          >
                            <AiOutlineClose className="text-xl" />
                          </button>
                        </div>

                        <div className="mt-4 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-blue-500 h-2 transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Uploading: {uploadProgress}%
                        </p>
                      </div>
                    )}

                  </div>
                )}

                {isUploadSuccess && (
                  <div></div>
                )}

                {errors && errors.length > 0 && (
                  <div className="mt-4">
                    <p className="text-red-500 font-medium">
                      Validation Errors:
                    </p>
                    <ul className="text-red-500 list-disc list-inside">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
        </FormModal>
      )}
      {isManualUploadOpen && (
        <ManualUpload projectId={projectId} toggleDialog={toggleManualUpload} />
      )}
    </div>
  );
};

export default BulkUploadProperties;